<div class="p-8 min-h-screen bg-white m-4 rounded-xl border border-gray-100 shadow-sm">
  <!-- Loading State -->
  <div *ngIf="loading" class="flex items-center justify-center min-h-screen">
    <div class="text-center">
      <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
      <p class="text-gray-600">Loading dashboard data...</p>
    </div>
  </div>

  <!-- Error State -->
  <div *ngIf="error && !loading" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
    <div class="flex items-center gap-2">
      <i class="fas fa-exclamation-circle text-red-600"></i>
      <p class="text-red-800">{{ error }}</p>
      <button (click)="loadDashboardData()" class="ml-auto text-sm text-red-600 hover:text-red-800 underline">
        Retry
      </button>
    </div>
  </div>

  <!-- Main Content -->
  <div *ngIf="!loading">
    <!-- Top Header Bar -->
    <div class="mb-8">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 mb-1">Welcome Back {{ user?.fullName?.split(' ')?.[0] || 'Admin' }}!</h1>
        <p class="text-sm text-gray-600">You've handled {{ stats.completedToday }} service request{{ stats.completedToday !== 1 ? 's' : '' }} today - great work!</p>
      </div>
    </div>

  <!-- Highlights Section - 4 Metric Cards -->
  <div class="mb-6">
    <div class="flex items-center justify-between mb-4">
      <h2 class="text-lg font-semibold text-gray-900">Highlights</h2>
      <button (click)="refreshData()" [disabled]="loading" class="text-sm text-gray-600 hover:text-gray-900 px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
        <i class="fas fa-sync-alt mr-2" [class.animate-spin]="loading"></i>
        Refresh Data
      </button>
    </div>
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <!-- Active Service Requests -->
      <div class="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-sm font-medium text-gray-600">Active Requests</h3>
          <div class="flex gap-0.5">
            <div class="w-1 h-3 bg-primary rounded"></div>
            <div class="w-1 h-4 bg-primary rounded"></div>
            <div class="w-1 h-2 bg-primary rounded"></div>
            <div class="w-1 h-3.5 bg-primary rounded"></div>
          </div>
        </div>
        <div class="flex items-baseline gap-2">
          <span class="text-3xl font-bold text-gray-900">{{ stats.activeRequests }}</span>
          <span *ngIf="stats.activeRequestsChange !== 0" class="text-sm font-medium" [ngClass]="stats.activeRequestsChange > 0 ? 'text-green-600' : 'text-red-600'">
            {{ stats.activeRequestsChange > 0 ? '+' : '' }}{{ stats.activeRequestsChange }}%
          </span>
        </div>
      </div>

      <!-- Services Completed -->
      <div class="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-sm font-medium text-gray-600">Completed Today</h3>
          <div class="w-12 h-8 flex items-end gap-0.5">
            <div class="w-2 bg-primary rounded-t" style="height: 40%"></div>
            <div class="w-2 bg-primary rounded-t" style="height: 60%"></div>
            <div class="w-2 bg-primary rounded-t" style="height: 80%"></div>
            <div class="w-2 bg-primary rounded-t" style="height: 100%"></div>
          </div>
        </div>
        <div class="flex items-baseline gap-2">
          <span class="text-3xl font-bold text-gray-900">{{ stats.completedToday }}</span>
          <span *ngIf="stats.completedChange !== 0" class="text-sm font-medium" [ngClass]="stats.completedChange > 0 ? 'text-green-600' : 'text-red-600'">
            {{ stats.completedChange > 0 ? '+' : '' }}{{ stats.completedChange }}%
          </span>
        </div>
      </div>

      <!-- Monthly Revenue -->
      <div class="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-sm font-medium text-gray-600">Monthly Revenue</h3>
          <div class="w-12 h-8 flex items-center">
            <svg viewBox="0 0 48 24" class="w-full h-full">
              <path d="M0,20 Q12,10 24,12 T48,8" stroke="var(--primary-blue)" stroke-width="2" fill="none"/>
            </svg>
          </div>
        </div>
        <div class="flex items-baseline gap-2">
          <span class="text-3xl font-bold text-gray-900">{{ formatCurrency(stats.monthlyRevenue) }}</span>
          <span *ngIf="stats.revenueChange !== 0" class="text-sm font-medium" [ngClass]="stats.revenueChange > 0 ? 'text-green-600' : 'text-red-600'">
            {{ stats.revenueChange > 0 ? '+' : '' }}{{ stats.revenueChange }}%
          </span>
        </div>
      </div>

      <!-- Active Drivers -->
      <div class="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-sm font-medium text-gray-600">Active Drivers</h3>
          <div class="flex gap-1">
            <div class="w-3 h-3 bg-primary rounded-full"></div>
            <div class="w-3 h-3 bg-primary rounded-full"></div>
            <div class="w-3 h-3 bg-primary rounded-full"></div>
            <div class="w-3 h-3 bg-primary rounded-full"></div>
            <div class="w-3 h-3 border-2 border-primary rounded-full"></div>
          </div>
        </div>
        <div class="flex items-baseline gap-2">
          <span class="text-3xl font-bold text-gray-900">{{ stats.activeDrivers }}</span>
          <span *ngIf="stats.driversChange !== 0" class="text-sm font-medium" [ngClass]="stats.driversChange > 0 ? 'text-green-600' : 'text-red-600'">
            {{ stats.driversChange > 0 ? '+' : '' }}{{ stats.driversChange }}
          </span>
        </div>
      </div>
    </div>
  </div>

  <!-- Main Content Grid -->
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
    <!-- Progress Overview Chart -->
    <div class="lg:col-span-2 bg-white rounded-xl p-6 shadow-sm border border-gray-200">
      <div class="flex items-center justify-between mb-6">
        <div>
          <h2 class="text-lg font-semibold text-gray-900 mb-1">Service Requests Overview</h2>
          <p class="text-sm text-gray-500">Daily service requests and completion trends.</p>
        </div>
        <div class="flex gap-2">
          <select class="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white">
            <option>All Services</option>
            <option>Towing</option>
            <option>Roadside Assistance</option>
            <option>Jump Start</option>
          </select>
          <select class="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white">
            <option>This Month</option>
            <option>Last Month</option>
            <option>This Week</option>
          </select>
        </div>
      </div>
      <div class="h-64 bg-gray-50 rounded-lg p-4">
        <canvas baseChart
          [data]="lineChartData"
          [options]="lineChartOptions"
          [type]="lineChartType">
        </canvas>
      </div>
    </div>

    <!-- Weekly Activity Split -->
    <div class="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-lg font-semibold text-gray-900">Service Type Breakdown</h2>
        <div class="flex items-center gap-2">
          <i class="fas fa-chevron-left text-gray-400 cursor-pointer"></i>
          <i class="fas fa-calendar text-gray-400 cursor-pointer"></i>
          <i class="fas fa-chevron-right text-gray-400 cursor-pointer"></i>
        </div>
      </div>
      <div class="h-64">
        <canvas *ngIf="serviceBreakdown.total > 0" baseChart
          [data]="pieChartData"
          [options]="pieChartOptions"
          [type]="pieChartType">
        </canvas>
        <div *ngIf="serviceBreakdown.total === 0" class="h-full flex items-center justify-center text-gray-500">
          No service data available
        </div>
      </div>
    </div>
  </div>

  <!-- Bottom Section Grid -->
  <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
    <!-- Active Service Requests -->
    <div class="lg:col-span-2 bg-white rounded-xl p-6 shadow-sm border border-gray-200">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-lg font-semibold text-gray-900">Active Service Requests</h2>
        <div class="flex items-center gap-2">
          <i class="fas fa-search text-gray-400 cursor-pointer"></i>
          <button class="text-sm text-gray-600 px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50">Sort</button>
          <button class="text-sm text-gray-600 px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50">Filter</button>
        </div>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="border-b border-gray-200">
              <th class="text-left py-3 px-4 text-sm font-medium text-gray-600">Customer / Location</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-gray-600">Request Time</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-gray-600">Service Type</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-gray-600">Status</th>
              <th class="text-left py-3 px-4 text-sm font-medium text-gray-600">Driver</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr *ngFor="let job of recentActiveJobs" class="hover:bg-gray-50">
              <td class="py-3 px-4 text-sm text-gray-900">
                {{ job.clientName || 'N/A' }} - {{ job.pickupLocation || 'Location not specified' }}
              </td>
              <td class="py-3 px-4 text-sm text-gray-600">{{ formatDate(job.createdAt) }}</td>
              <td class="py-3 px-4 text-sm text-gray-600">{{ job.serviceType || 'N/A' }}</td>
              <td class="py-3 px-4">
                <span class="text-sm" [ngClass]="getStatusColor(job.status)">{{ job.status }}</span>
              </td>
              <td class="py-3 px-4">
                <span class="text-sm text-gray-900 font-medium">{{ job.driverName || '-' }}</span>
              </td>
            </tr>
            <tr *ngIf="recentActiveJobs.length === 0">
              <td colspan="5" class="py-8 px-4 text-center text-gray-500">
                No active service requests at the moment
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Quick Actions -->
    <div class="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
      <h2 class="text-lg font-semibold text-gray-900 mb-1">Quick Actions</h2>
      <p class="text-sm text-gray-600 mb-4">Manage your towing operations efficiently!</p>
      
      <div class="mb-4">
        <input 
          type="text" 
          placeholder="Search service request..." 
          class="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>
      
      <div class="mb-4">
        <p class="text-xs text-gray-500 mb-1">Recent Activity:</p>
        <p *ngIf="recentActiveJobs.length > 0" class="text-sm text-gray-900 font-medium">
          Job #{{ recentActiveJobs[0].id }} - {{ recentActiveJobs[0].status }}
        </p>
        <p *ngIf="recentActiveJobs.length === 0" class="text-sm text-gray-500">No recent activity</p>
      </div>
      
      <div class="flex items-center gap-2 mb-4">
        <div class="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
          <i class="fas fa-truck text-sm text-primary"></i>
        </div>
        <div class="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
          <i class="fas fa-user text-gray-600 text-sm"></i>
        </div>
        <div class="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
          <i class="fas fa-map-marker-alt text-gray-600 text-sm"></i>
        </div>
        <div class="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
          <i class="fas fa-chart-line text-gray-600 text-sm"></i>
        </div>
        <div class="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
          <i class="fas fa-cog text-gray-600 text-sm"></i>
        </div>
        <i class="fas fa-arrow-right text-gray-400 ml-2"></i>
      </div>
      
      <div class="flex gap-2">
        <button class="flex-1 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors">
          View Reports
        </button>
        <button class="flex-1 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:opacity-90 transition-colors flex items-center justify-center gap-2">
          New Request
          <i class="fas fa-plus text-xs"></i>
        </button>
      </div>
    </div>
    </div>
  </div>
</div>

